import React from "react";
import { Reveal } from "../components/motion";
import { useLang } from "../i18n";

const COPY = {
  fr: {
    title: "Conditions générales",
    eyebrow: "Dernière mise a jour : Octobre 2026",
    intro:
      "Merci de prendre le temps de lire ces conditions. En vous abonnant ou en effectuant un achat sur Nordicvy, vous acceptez l'ensemble des points ci-dessous.",
    sections: [
      {
        h: "1. Nature du service",
        p: "Nordicvy est un service d'accompagnement par les pairs, orienté retablissement et sensible au trauma. L'accompagnatrice principale est certifiee CPMC et CPMC-T. Les services, revues et contenus proposes ne constituent pas un avis medical, un diagnostic ou un traitement. Ils ne remplacent pas le suivi d'un professionnel de la sante.",
      },
      {
        h: "2. Abonnements recurrents",
        p: "Les forfaits Basic et Decouverte sont factures de maniere recurrente (mensuelle ou annuelle) via Stripe. Le renouvellement est automatique a la fin de chaque periode. Vous pouvez annuler votre abonnement a tout moment depuis le portail client Stripe (lien dans votre courriel de confirmation). L'annulation prend effet a la fin de la periode payee en cours.",
      },
      {
        h: "3. Achats a l'unite",
        p: "Les produits achetes a l'unite (par exemple le Guide pratique) sont livres immediatement par courriel sous forme de fichier PDF.",
      },
      {
        h: "4. Politique de non-remboursement",
        p: "Tous les abonnements et achats sont fermes et non remboursables, en raison de la nature numerique et immediate du contenu livre. Aucun remboursement partiel ou total ne sera accorde apres l'activation d'un abonnement ou le telechargement d'un produit numerique. Pour cesser d'etre facturee, utilisez l'annulation en 1 clic via le portail Stripe.",
      },
      {
        h: "5. Seances individuelles",
        p: "Toute seance reservee doit etre payee a l'avance et honoree a l'heure convenue. En cas d'annulation moins de 24 heures avant la seance, aucun remboursement ou report ne sera accorde. En cas de reprogrammation avec plus de 24 heures d'avis, un seul report est possible dans les 30 jours suivants.",
      },
      {
        h: "6. Confidentialite",
        p: "Vos donnees personnelles (nom, courriel, informations de paiement) sont traitees de maniere confidentielle. Les paiements sont geres directement par Stripe (certifie PCI DSS). Nordicvy ne stocke jamais les informations de carte. Pour toute demande d'acces ou de suppression de vos donnees, ecrivez a : contact@nordicvy.ca.",
      },
      {
        h: "7. Propriete intellectuelle",
        p: "L'ensemble des contenus publies sur Nordicvy (textes, images, revues, guides, recettes, enregistrements) est protege par le droit d'auteur. Toute reproduction, diffusion ou revente est strictement interdite sans autorisation ecrite prealable.",
      },
      {
        h: "8. Securite et bien-etre",
        p: "Si vous traversez une periode de crise ou avez des pensees suicidaires, Nordicvy n'est pas un service d'urgence. Veuillez contacter immediatement : 811 (Info-Social, Quebec), 1 866 277-3553 (ligne quebecoise de prevention du suicide) ou le 911.",
      },
      {
        h: "9. Loi applicable",
        p: "Les presentes conditions sont regies par les lois en vigueur au Quebec, Canada. Tout litige sera soumis a la juridiction des tribunaux de Montreal.",
      },
      {
        h: "10. Modifications",
        p: "Nordicvy se reserve le droit de modifier ces conditions. Les clientes seront informees par courriel de tout changement significatif.",
      },
    ],
    accept:
      "En cochant la case au moment du paiement, vous confirmez avoir lu, compris et accepte ces conditions.",
  },
  en: {
    title: "Terms & Conditions",
    eyebrow: "Last updated: October 2026",
    intro:
      "Thank you for taking the time to read these terms. By subscribing or making a purchase on Nordicvy, you accept all the points below.",
    sections: [
      {
        h: "1. Nature of the service",
        p: "Nordicvy is a peer support service, recovery-oriented and trauma-informed. The lead supporter is CPMC and CPMC-T certified. The services, reviews and content offered do not constitute medical advice, diagnosis or treatment. They do not replace follow-up with a healthcare professional.",
      },
      {
        h: "2. Recurring subscriptions",
        p: "The Basic and Decouverte plans are billed on a recurring basis (monthly or yearly) via Stripe. Renewal is automatic at the end of each period. You can cancel your subscription at any time from the Stripe customer portal (link in your confirmation email). Cancellation takes effect at the end of the current paid period.",
      },
      {
        h: "3. One-time purchases",
        p: "Products purchased individually (for example the Practical Guide) are delivered immediately by email as a PDF file.",
      },
      {
        h: "4. No-refund policy",
        p: "All subscriptions and purchases are final and non-refundable, due to the digital and immediate nature of the content delivered. No partial or full refund will be granted after activation of a subscription or download of a digital product. To stop billing, use the 1-click cancellation via the Stripe portal.",
      },
      {
        h: "5. One-on-one sessions",
        p: "Any booked session must be paid in advance and attended at the agreed time. In case of cancellation less than 24 hours before the session, no refund or rescheduling will be granted. For rescheduling with more than 24 hours notice, one reschedule is possible within the following 30 days.",
      },
      {
        h: "6. Privacy",
        p: "Your personal data (name, email, payment information) is treated confidentially. Payments are managed directly by Stripe (PCI DSS certified). Nordicvy never stores card information. For any request to access or delete your data, write to: contact@nordicvy.ca.",
      },
      {
        h: "7. Intellectual property",
        p: "All content published on Nordicvy (text, images, reviews, guides, recipes, recordings) is protected by copyright. Any reproduction, distribution or resale is strictly prohibited without prior written authorization.",
      },
      {
        h: "8. Safety and well-being",
        p: "If you are going through a crisis or having suicidal thoughts, Nordicvy is not an emergency service. Please contact immediately: 811 (Info-Social, Quebec), 1 833 456-4566 (Canada Suicide Prevention Service) or 911.",
      },
      {
        h: "9. Governing law",
        p: "These terms are governed by the laws in force in Quebec, Canada. Any dispute will be submitted to the jurisdiction of the courts of Montreal.",
      },
      {
        h: "10. Changes",
        p: "Nordicvy reserves the right to modify these terms. Clients will be notified by email of any significant changes.",
      },
    ],
    accept:
      "By checking the box at the time of payment, you confirm that you have read, understood and accepted these terms.",
  },
};

const Terms = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.fr;
  return (
    <div data-testid="terms-page" className="pt-36 pb-28 max-w-3xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-primary/70 text-xs uppercase tracking-[0.25em] mb-6">{c.eyebrow}</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tightest text-foreground mb-8">{c.title}</h1>
        <p className="text-foreground/70 text-lg leading-relaxed mb-12">{c.intro}</p>
      </Reveal>
      <div className="space-y-10">
        {c.sections.map((s, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <h2 className="font-serif text-2xl text-foreground mb-3">{s.h}</h2>
            <p className="text-foreground/70 leading-relaxed">{s.p}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.3}>
        <div className="mt-14 p-6 border-l-4 border-primary bg-muted">
          <p className="text-sm text-foreground/80 italic">{c.accept}</p>
        </div>
        <p className="mt-10 text-xs text-foreground/50">© {new Date().getFullYear()} Nordicvy.</p>
      </Reveal>
    </div>
  );
};

export default Terms;
